import { React, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AbdsButton } from '@abds/react-bindings';
import { createHttpClient, handleRemoteError } from '../../utilities/httpclient';
import createUserSessionActivity from '../../utilities/usersessiontracking';
import { createDisplayAreaMessengerContext, DisplayAreaMessenger } from '../displayareamessenger/DisplayAreaMessenger';
import { APPLICATION_API_ENDPOINT, checkUserTokenFormat } from '../../utilities/constants';
import { formatDate } from '../../utilities/general';

import '../../styles/main.css';
import '../../styles/searchquerycontainer.css';
import '../../styles/absgtable.css';
import '../../styles/certificatedetails.css';

export function X12Details(props)
{
    let [messageContext, setMessageContext] = useState(createDisplayAreaMessengerContext());
    let [headerData, setHeaderData] = useState({});
    let [fileData, setFileData] = useState([]);
    let [messageData, setMessageData] = useState([]);
    let [statusData, setStatusData] = useState({});
    let [showAllMessages, setShowAllMessages] = useState(true);
    let [responseData, setResponseData] = useState({});

    const params = useParams();

    const navigate = useNavigate();

    const showErrorMessage = msg => {
        setMessageContext({});
        messageContext = createDisplayAreaMessengerContext();
        messageContext.error.message = msg;
        setMessageContext(messageContext);
    };

    const handleDashboardButtonOnClick = () => {
        navigate('/x12'); //return to dashboard
        return;
    };

    const getX12CaseMessageDataByKey = async key => {
        if (!checkUserTokenFormat(key))
            handleDashboardButtonOnClick();

        const httpclient = createHttpClient();
        await httpclient.post(`${APPLICATION_API_ENDPOINT}/x12/getcase`, { 'X12CaseGUIDAsString': key }).then(response => {
            setResponseData(response.data);
            setHeaderData(response.data.x12CaseHeader);
            setStatusData(response.data.x12CaseStatus);
            setFileData(response.data.x12CaseFiles);
            filterCaseMessageDetails(response.data.x12CaseMessageDetails);
        }).catch(e => (handleRemoteError(navigate, e) || showErrorMessage(e.response?.data || e.message)));
    };

    const onChangeShowAllMessages = e => setShowAllMessages(!!e.target.checked);

    const filterCaseMessageDetails = freshData => {
        const msgData = freshData?.filter(item => (showAllMessages === true || (!showAllMessages && !item.success)));
        setMessageData(msgData);
    };

    useEffect(() => {
        (async () => {
            createUserSessionActivity(window.location.pathname);
            await getX12CaseMessageDataByKey(params.x12CaseGUID);
        })();
    }, []);

    useEffect(() => {
        filterCaseMessageDetails(responseData.x12CaseMessageDetails);
    }, [showAllMessages]);

    return (
        <div>
            <h3>X12 Validator: Case Details</h3>
            <hr className="abcblue" />

            <DisplayAreaMessenger context={messageContext} />

            <h4>Case Details</h4>

            {
                (!headerData || Object.keys(headerData).length === 0) && (
                    <p className="centered">No header data</p>
                )
            }
            {
                (Object.keys(headerData).length > 0) && (
                    <>
                        <div className="rightside">
                            <AbdsButton onClick={handleDashboardButtonOnClick}>Return to X12 Dashboard</AbdsButton>
                        </div>
                        <hr className="abcblue" />

                        <table data-role="subdetailheader">
                            <thead>
                                <tr>
                                    <th>Case Name:</th>
                                    <th>RITM:</th>
                                    <th>Status:</th>
                                    <th>Created:</th>
                                    <th>Processed:</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{headerData.caseName}</td>
                                    <td>{headerData.ritm}</td>
                                    <td>{statusData.label}</td>
                                    <td>{formatDate(headerData.processedDateTime)}</td>
                                    <td>{formatDate(headerData.createdByDateTime)}</td>
                                </tr>
                                {
                                    headerData.errorMessage?.length > 0 && (
                                        <>
                                            <tr>
                                                <td>Error:</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {
                                                        (headerData.errorMessage?.length > 0) &&
                                                        (
                                                            <>{headerData.errorMessage}</>
                                                        )
                                                    }
                                                    {
                                                        (!headerData.errorMessage || headerData.errorMessage.length === 0) &&
                                                        (
                                                            <>(none)</>
                                                        )
                                                    }
                                                </td>
                                            </tr>
                                        </>
                                    )
                                }
                            </tbody>
                        </table>
                    </>
                )
            }

            <hr className="abcblue" />
            <h4>Case Files</h4>

            {
                (fileData?.length === 0) && (
                    <>
                        <hr className="abcblue" />
                        <p className="centered">No file details</p>
                    </>
                )
            }

            {
                !!fileData && fileData.length > 0 && (
                    <>
                        <hr className="abcblue" />
                        <table className="absgtable auxiliary">
                            <thead>
                                <tr>
                                    <th>File Type</th>
                                    <th>File Name</th>
                                    <th>Error Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    fileData.map(item => {
                                        return (
                                            <tr key={item.x12CaseFileGUID}>
                                                <td style={ {width:'25%'} }>{item.applicationTypeLabel}</td>
                                                <td style={ {width:'15%' }}>{item.fileName}</td>
                                                <td>{item.errorMessage}</td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </>
                )
            }

            <hr className="abcblue" />
            <h4>Case Messages</h4>
            <hr className="abcblue" />
            <div className="rightside">
                <input type="checkbox" checked={showAllMessages} onChange={onChangeShowAllMessages} name="rShowAllMessages" />&nbsp;Show All Messaging
            </div>
            {
                (messageData?.length === 0) && (
                    <>
                        <hr className="abcblue" />
                        <p className="centered">No message details</p>
                    </>
                )
            }

            {
                !!messageData && messageData.length > 0 && (
                    <>
                        <table className="absgtable auxiliary">
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Source File</th>
                                    <th>Compare File</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    messageData.map(item => {
                                        return (
                                            <tr key={item.x12CaseMessageGUID}>
                                                <td style={{'width':'150px'}}>
                                                    {
                                                        item.errored && (
                                                            <>
                                                                <span className="bi bi-x-octagon-fill crimson"></span>
                                                                <span className="statuslabel crimson">{item.statusLabel}</span>
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        item.failed && (
                                                            <>
                                                                <span className="bi bi-x-square-fill redorange"></span>
                                                                <span className="statuslabel redorange">{item.statusLabel}</span>
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        item.softWarning && (
                                                            <>
                                                                <span className="bi bi-exclamation-circle-fill yellow"></span>
                                                                <span className="statuslabel yellow">{item.statusLabel}</span>
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        item.hardWarning && (
                                                            <>
                                                                <span className="bi bi-exclamation-circle-fill orange"></span>
                                                                <span className="statuslabel orange">{item.statusLabel}</span>
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        item.success && (
                                                            <>
                                                                <span className="bi bi-check-circle-fill green"></span>
                                                                <span className="statuslabel green">{item.statusLabel}</span>
                                                            </>
                                                        )
                                                    }
                                                </td>
                                                <td>{item.sourceFileName}</td>
                                                <td>{item.compareFileName}</td>
                                                <td>{item.message}</td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </>
                )
            }
            <hr className="abcblue" />
            <div className="rightside">
                <AbdsButton onClick={handleDashboardButtonOnClick}>Return to X12 Dashboard</AbdsButton>
            </div>
        </div>
    );
}